/**
 * El paso 2: mientras el backend lee la lista.
 *
 * POR QUE NO ES UN SPINNER
 * ------------------------
 * `POST /listas/import` contesta 202 y sigue trabajando de fondo. Leer un PDF
 * mayorista de cuarenta paginas con la Claude API tarda minutos. Un spinner sin
 * texto durante dos minutos se lee como "se colgo", y lo que hace la gente
 * cuando algo parece colgado es recargar o volver a subir el archivo — es decir,
 * pagar la extraccion dos veces y quedarse con dos imports.
 *
 * Asi que se muestra lo que de verdad se sabe: en que paso esta (los tres del
 * backend, en orden), el porcentaje exacto de la subida (el unico medible), y el
 * tiempo transcurrido contando de verdad. Cuando termina, se muestra cuanto
 * tardo, cuantas filas salieron y con que modelo, que es lo que hace falta para
 * saber si conviene mandar el archivo de otra forma la proxima vez.
 */
import { useEffect, useState } from "react";
import { Check, CircleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/cargando";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";

import {
  PASOS,
  estaTrabajando,
  formatearBytes,
  formatearDuracion,
  pasoActual,
  type ImportLista,
} from "./logica";
import { aNumero } from "@/lib/tipos";

export interface ProcesoImportProps {
  imp: ImportLista | null;
  /** Nombre del archivo mientras todavia no hay import en el servidor. */
  archivoNombre: string | null;
  subiendo: boolean;
  progresoSubida: number;
  onEmpezarDeNuevo: () => void;
}

/** Segundos desde que arranco, actualizados a mano. No hay progreso que animar. */
function useTranscurrido(desde: string | null, activo: boolean): number {
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    if (!activo) return;
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activo]);

  if (desde === null) return 0;
  const inicio = new Date(desde).getTime();
  if (Number.isNaN(inicio)) return 0;
  return Math.max(0, ahora - inicio);
}

export function ProcesoImport({
  imp,
  archivoNombre,
  subiendo,
  progresoSubida,
  onEmpezarDeNuevo,
}: ProcesoImportProps): JSX.Element {
  const estado = imp?.estado ?? null;
  const trabajando = subiendo || estaTrabajando(estado);
  const indice = pasoActual(estado, subiendo);
  const transcurrido = useTranscurrido(imp?.created_at ?? null, trabajando);

  if (estado === "ERROR" && imp !== null) {
    return <ProcesoFallido imp={imp} onEmpezarDeNuevo={onEmpezarDeNuevo} />;
  }

  return (
    <Card padding="normal" className="space-y-4 sm:p-6">
      <TituloSeccion
        acciones={
          <span className="text-xs tabular-nums text-muted-foreground">
            {archivoNombre ?? imp?.archivo_nombre ?? "archivo"}
            {imp ? ` · ${formatearBytes(aNumero(imp.archivo_bytes))}` : ""}
          </span>
        }
      >
        {trabajando ? "Leyendo la lista" : "La lista quedó lista para revisar"}
      </TituloSeccion>

      <ol className="space-y-3">
        {PASOS.map((paso, i) => {
          const hecho = i < indice;
          const enCurso = i === indice && trabajando;
          return (
            <li key={paso.clave} className="flex gap-3">
              <span
                className={cn(
                  "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border",
                  hecho && "border-success bg-success text-success-foreground",
                  enCurso && "border-ring text-foreground",
                  !hecho && !enCurso && "border-border text-muted-foreground",
                )}
              >
                {hecho ? (
                  <Check className="size-3" aria-hidden="true" />
                ) : enCurso ? (
                  <Spinner className="size-3" />
                ) : (
                  <span className="text-xs tabular-nums">{i + 1}</span>
                )}
              </span>
              <div className="min-w-0 space-y-0.5">
                <p
                  className={cn(
                    "text-sm",
                    hecho || enCurso ? "font-medium" : "text-muted-foreground",
                  )}
                >
                  {paso.titulo}
                  {paso.clave === "SUBIENDO" && subiendo ? (
                    <span className="ml-2 tabular-nums text-muted-foreground">
                      {progresoSubida} %
                    </span>
                  ) : null}
                </p>
                {enCurso ? <p className="text-xs text-muted-foreground">{paso.detalle}</p> : null}
              </div>
            </li>
          );
        })}
      </ol>

      {trabajando ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="tabular-nums">
            Van {formatearDuracion(transcurrido)}. Podés dejar esta pantalla abierta.
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={onEmpezarDeNuevo}>
            Cancelar y empezar de nuevo
          </Button>
        </div>
      ) : imp ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border pt-3 text-xs sm:grid-cols-4">
          <Dato titulo="Filas leídas" valor={String(imp.filas_total)} />
          <Dato
            titulo="Tardó"
            valor={imp.duracion_ms === null ? "—" : formatearDuracion(imp.duracion_ms)}
          />
          <Dato titulo="Moneda detectada" valor={imp.moneda_detectada ?? "—"} />
          <Dato titulo="Modelo" valor={imp.modelo ?? "—"} />
        </dl>
      ) : null}

      {imp?.notas ? (
        <Card variant="muted" padding="compacto" className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Lo que anotó la IA: </span>
          {imp.notas}
        </Card>
      ) : null}
    </Card>
  );
}

function Dato({ titulo, valor }: { titulo: string; valor: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="truncate text-muted-foreground">{titulo}</dt>
      <dd className="truncate font-medium tabular-nums">{valor}</dd>
    </div>
  );
}

/**
 * La extraccion fallo.
 *
 * El `error_mensaje` del backend se muestra entero: dice si el archivo era
 * ilegible, si se paso del limite de uso de la API o si el formato no se
 * soporta, y cada uno se resuelve distinto. El `error_codigo` va al lado, chico,
 * porque es lo que se busca cuando hay que preguntar.
 */
function ProcesoFallido({
  imp,
  onEmpezarDeNuevo,
}: {
  imp: ImportLista;
  onEmpezarDeNuevo: () => void;
}): JSX.Element {
  return (
    <Card tono="destructive" padding="normal" className="space-y-3 sm:p-6">
      <div className="flex items-start gap-2">
        <CircleAlert className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <TituloSeccion>No pudimos leer «{imp.archivo_nombre}»</TituloSeccion>
          <p className="text-sm text-muted-foreground">
            {imp.error_mensaje ?? "La extracción falló sin dejar detalle."}
          </p>
          {imp.error_codigo ? (
            <p className="text-xs tabular-nums text-muted-foreground">Código: {imp.error_codigo}</p>
          ) : null}
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        No se tocó ningún precio. Si el archivo es una foto, sacá una más derecha y con la lista
        entera en el cuadro; si es un PDF de muchas páginas, probá dividirlo.
      </p>
      <Button type="button" variant="outline" onClick={onEmpezarDeNuevo}>
        Probar con otro archivo
      </Button>
    </Card>
  );
}
